import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@aqarmap/aqarmap-ui';
import {
  useGetUserQuery,
  useUpdatePersonalInfoMutation,
} from '~/redux/api/userApiSlice';
import { useForm } from 'react-hook-form';
import ErrorMessage from '~/components/ErrorMessage';
import { PersonalInfoItems } from '~myAccount/enums';
import LayoutSkeleton from '~/components/LayoutSkeleton';
import { objectSerialize } from '~/lib';
import PageLayout from '~/modules/myAccount/components/PageLayout';
import { useFullRoutes } from '~/hooks';
import Question from '~myAccount/pages/PersonalInfo/PersonalInfoDetails/Question';

type FormDataType = {
  age: string;
  education: string;
  gender: string;
  industry: string;
  maritalStatus: string;
  position: string;
  purchaseInfo: string;
  purchase_purpose: string;
  purchase_financing: string;
  purchase_timeframe: string;
};

const sections = [
  {
    title: 'basicInformation',
    items: [
      { label: PersonalInfoItems.AGE, name: 'age' },
      { label: PersonalInfoItems.GENDER, name: 'gender' },
      { label: PersonalInfoItems.MARITAL_STATUS, name: 'maritalStatus' },
    ],
  },
  {
    title: 'educationAndProfession',
    items: [
      { label: PersonalInfoItems.EDUCATION, name: 'education' },
      { label: PersonalInfoItems.INDUSTRY, name: 'industry' },
      { label: PersonalInfoItems.POSITION, name: 'position' },
    ],
  },
  {
    title: 'purchasePreferences',
    items: [
      {
        label: PersonalInfoItems.PURCHASE_TIME_FRAME,
        name: 'purchase_timeframe',
      },
      {
        label: PersonalInfoItems.PURCHASE_PURPOSE,
        name: 'purchase_purpose',
      },
      {
        label: PersonalInfoItems.PURCHASE_FINANCING,
        name: 'purchase_financing',
      },
    ],
  },
] as const;

const PersonalInfoDetails = () => {
  const navigate = useNavigate();
  const { MY_ACCOUNT_URL } = useFullRoutes();
  const {
    data: response,
    isLoading: userIsLoading,
    isSuccess: userIsSuccess,
  } = useGetUserQuery();
  const personalInfoIndex =
    Number(response?.user?.personal_info?.length) - 1 || 0;

  const age = response?.user.personal_info[personalInfoIndex]?.age;
  const gender = response?.user.personal_info[personalInfoIndex]?.gender;
  const maritalStatus =
    response?.user.personal_info[personalInfoIndex]?.marital_status;
  const education = response?.user.personal_info[personalInfoIndex]?.education;
  const industry = response?.user.personal_info[personalInfoIndex]?.industry;
  const position = response?.user.personal_info[personalInfoIndex]?.position;
  const purchaseInfo =
    response?.user.personal_info[personalInfoIndex]?.purchase_info;
  const purchasePurpose =
    response?.user.personal_info[personalInfoIndex]?.purchase_purpose;
  const purchaseFinancing =
    response?.user.personal_info[personalInfoIndex]?.purchase_financing;
  const purchaseTimeframe =
    response?.user.personal_info[personalInfoIndex]?.purchase_timeframe;

  const { t } = useTranslation('app');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    basicInformation: true,
    educationAndProfession: true,
    purchasePreferences: true,
  });

  const [
    updatePersonalInfo,
    {
      isLoading: updateUserIsLoading,
      isError: updatePersonalInfoIsError,
      error: updatePersonalInfoError,
    },
  ] = useUpdatePersonalInfoMutation();

  const { handleSubmit, control } = useForm<FormDataType>({
    values: {
      age: age ?? '',
      education: education ?? '',
      gender: gender ?? '',
      industry: industry ?? '',
      maritalStatus: maritalStatus ?? '',
      position: position ?? '',
      purchaseInfo: purchaseInfo ?? '',
      purchase_purpose: purchasePurpose ?? '',
      purchase_financing: purchaseFinancing ?? '',
      purchase_timeframe: purchaseTimeframe ?? '',
    },
  });

  const onSubmit = async ({
    age,
    education,
    gender,
    industry,
    maritalStatus,
    position,
    /* purchaseInfo, */
    purchase_financing,
    purchase_purpose,
    purchase_timeframe,
  }: FormDataType) => {
    const formData = objectSerialize({
      'aqarmap_bundle_userbundle_personalinfo[age]': age,
      'aqarmap_bundle_userbundle_personalinfo[maritalStatus]': maritalStatus,
      'aqarmap_bundle_userbundle_personalinfo[position]': position,
      /* 'aqarmap_bundle_userbundle_personalinfo[purchaseInfo]': purchaseInfo, */
      'aqarmap_bundle_userbundle_personalinfo[education]': education,
      'aqarmap_bundle_userbundle_personalinfo[industry]': industry,
      'aqarmap_bundle_userbundle_personalinfo[gender]': gender,
      'aqarmap_bundle_userbundle_personalinfo[purchasePurpose]':
        purchase_purpose,
      'aqarmap_bundle_userbundle_personalinfo[purchaseFinancing]':
        purchase_financing,
      'aqarmap_bundle_userbundle_personalinfo[purchaseTimeframe]':
        purchase_timeframe,
    });
    const res = await updatePersonalInfo(formData);
    if ((res as any)?.data) {
      navigate(MY_ACCOUNT_URL.PERSONAL_INFO);
    }
  };
  const errorMessage = (updatePersonalInfoError as any)?.data?.message;

  return (
    <PageLayout
      title={t('personalInfo')}
      bodyClassName="border border-gray rounded-[8px]"
    >
      {userIsLoading && <LayoutSkeleton />}
      {updatePersonalInfoIsError && <ErrorMessage message={errorMessage} />}

      {!userIsLoading && userIsSuccess && (
        <form className="rounded-[8px]" onSubmit={handleSubmit(onSubmit)}>
          <div className="p-3x">
            {sections.map((section, sectionIndex) => (
              <div
                key={section.title}
                className={sectionIndex !== sections.length - 1 ? 'mb-3x' : ''}
              >
                <button
                  type="button"
                  className={`flex w-full items-center justify-between ${
                    openSections[section.title] ? 'mb-3x' : ''
                  }`}
                  aria-expanded={openSections[section.title]}
                  onClick={() =>
                    setOpenSections((currentSections) => ({
                      ...currentSections,
                      [section.title]: !currentSections[section.title],
                    }))
                  }
                >
                  <p className="text-title-4 text-black">{t(section.title)}</p>
                  <Icon
                    name="chevron-down"
                    className={`text-gray__dark_1 transition-transform ${
                      openSections[section.title] ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openSections[section.title] && (
                  <div className="flex flex-col gap-2x">
                    {section.items.map((item) => (
                      <Question
                        control={control}
                        label={item.label}
                        name={item.name}
                        questionText={t(String(item.label))}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center px-3x pb-3x gap-2x border-t border-gray pt-2x">
            <Button
              size="medium"
              ariaLabel="submit-btn"
              type="submit"
              variant="primary"
              isLoading={updateUserIsLoading}
              disabled={updateUserIsLoading}
              className="w-auto"
            >
              {t('save')}
            </Button>

            <Button
              size="medium"
              ariaLabel="cancel-btn"
              type="button"
              variant="secondary"
              className="w-auto"
              onClick={() => navigate(-1)}
            >
              {t('cancel')}
            </Button>
          </div>
        </form>
      )}
    </PageLayout>
  );
};

export default PersonalInfoDetails;
